import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, Award, BarChart3, Building2, Cpu, Crown, Loader2, RefreshCw, Search,
  Sparkles, Stethoscope, Target, TrendingDown, TrendingUp, Zap,
} from "lucide-react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ComposedChart,
  Legend, Line, LineChart, PolarAngleAxis, PolarGrid, PolarRadiusAxis,
  Radar, RadarChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCiroDate, formatCiroMoney } from "@/utils/ciroHelpers";
import { formatCompactMoney } from "@/components/finance/ciroCharts";
import "./ciro-dashboard2.css";

const YEAR_PALETTE = ["#34d399", "#38bdf8", "#a78bfa", "#fbbf24", "#fb7185", "#22d3ee"];
const ANIM = { duration: 1400, easing: "ease-out" };

const useAnimatedNumber = (target, active, duration = 1200) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const end = Number(target) || 0;
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      setValue(end * eased);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, active, duration]);
  return value;
};

const ChartTooltip = ({ active, payload, label, isDark }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={`ciro-d2-tooltip px-3 py-2 text-xs shadow-xl ${isDark ? "bg-slate-900/95 text-slate-100" : "bg-white/95 text-gray-800"}`}>
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }} className="tabular-nums">
          {p.name}: {typeof p.value === "number" && p.dataKey !== "growth_pct" && !String(p.dataKey).includes("ops_")
            ? formatCiroMoney(p.value)
            : p.value?.toLocaleString?.("tr-TR") ?? p.value}
        </p>
      ))}
    </div>
  );
};

const KpiTile = ({ label, value, display, tone, isDark, animate, icon: Icon, delta }) => (
  <div className={`ciro-d2-kpi ciro-d2-kpi-${tone} ${!isDark ? "ciro-d2-kpi-light" : ""}`}>
    <div className="flex items-start justify-between gap-2 mb-2">
      <p className={`text-[10px] uppercase tracking-widest ${isDark ? "text-slate-400" : "text-gray-500"}`}>{label}</p>
      {Icon ? <Icon className={`w-4 h-4 opacity-70 ${isDark ? "text-slate-300" : "text-gray-600"}`} /> : null}
    </div>
    <p className={`ciro-d2-value text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
      {display ?? formatCiroMoney(animate ? value : Number(value) || 0)}
    </p>
    {delta != null && (
      <p className={`text-[11px] mt-1 flex items-center gap-1 ${delta >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
        {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {delta >= 0 ? "+" : ""}{delta.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}% YoY
      </p>
    )}
  </div>
);

const PatternCard = ({ pattern, isDark }) => {
  const icons = {
    ciro_leader: Crown,
    ciro_gap: Target,
    volume_leader: Activity,
    avg_ticket_leader: Zap,
    above_average: Award,
    yoy_growth_leader: TrendingUp,
  };
  const Icon = icons[pattern.type] || Sparkles;
  const valueDisplay = pattern.type === "volume_leader"
    ? Number(pattern.value || 0).toLocaleString("tr-TR")
    : pattern.type === "yoy_growth_leader" || pattern.type === "above_average"
      ? (pattern.type === "yoy_growth_leader" ? `%${pattern.value}` : formatCiroMoney(pattern.value))
      : formatCiroMoney(pattern.value);

  return (
    <div className={`ciro-d2-pattern-card rounded-xl border p-3 ${isDark ? "border-slate-700/80 bg-slate-900/50" : "border-gray-200 bg-white/90"}`}>
      <div className="flex items-start gap-2">
        <div className={`p-1.5 rounded-lg ${isDark ? "bg-emerald-500/15" : "bg-emerald-50"}`}>
          <Icon className={`w-4 h-4 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[10px] uppercase tracking-wide font-semibold ${isDark ? "text-slate-400" : "text-gray-500"}`}>{pattern.title}</p>
          <p className={`text-sm font-bold truncate ${isDark ? "text-white" : "text-gray-900"}`}>{pattern.doctor}</p>
          <p className={`text-lg font-bold tabular-nums ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>{valueDisplay}</p>
          {pattern.detail && <p className={`text-[11px] mt-1 leading-relaxed ${isDark ? "text-slate-400" : "text-gray-600"}`}>{pattern.detail}</p>}
        </div>
      </div>
    </div>
  );
};

const SummaryHero = ({ insights, patterns, isDark, textClass, loading }) => (
  <div className={`ciro-d2-summary-hero rounded-2xl border p-4 md:p-5 ${isDark ? "border-emerald-500/25 bg-gradient-to-br from-emerald-500/10 via-slate-900/60 to-cyan-500/10" : "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-cyan-50"}`}>
    <div className="flex items-center gap-2 mb-4">
      <Sparkles className={`w-5 h-5 ${isDark ? "text-amber-400" : "text-amber-600"}`} />
      <h3 className={`text-base font-bold ${textClass}`}>Analiz Özeti</h3>
      <span className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>Canlı</span>
    </div>
    {loading ? (
      <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-emerald-500" /></div>
    ) : (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 mb-4">
          {(patterns || []).slice(0, 6).map((p, idx) => (
            <PatternCard key={`${p.type}-${idx}`} pattern={p} isDark={isDark} />
          ))}
        </div>
        <ul className="space-y-2 border-t pt-3 border-dashed border-emerald-500/20">
          {(insights || []).map((line, idx) => (
            <li key={idx} className="ciro-d2-insight flex gap-2" style={{ animationDelay: `${idx * 60}ms` }}>
              <TrendingUp className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
              <p className={`text-sm leading-relaxed ${isDark ? "text-slate-200" : "text-gray-800"}`}>{line}</p>
            </li>
          ))}
          {!insights?.length && !patterns?.length && (
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>Veri yüklendiğinde özet burada görünür.</p>
          )}
        </ul>
      </>
    )}
  </div>
);

const HeatmapGrid = ({ cells, years, isDark }) => {
  const monthLabels = ["O", "Ş", "M", "N", "M", "H", "T", "A", "E", "E", "K", "A"];
  const matrix = useMemo(() => {
    const map = {};
    (cells || []).forEach((c) => {
      map[`${c.year}-${c.month}`] = c;
    });
    return map;
  }, [cells]);

  if (!years?.length) {
    return <p className={`text-xs py-6 text-center ${isDark ? "text-slate-400" : "text-gray-500"}`}>Isı haritası verisi yok</p>;
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[420px]">
        <div className="grid gap-1" style={{ gridTemplateColumns: `48px repeat(${years.length}, minmax(52px, 1fr))` }}>
          <div />
          {years.map((y, yi) => (
            <div key={y} className="text-center text-[10px] font-semibold pb-1" style={{ color: YEAR_PALETTE[yi % YEAR_PALETTE.length] }}>
              {y}
            </div>
          ))}
          {monthLabels.map((ml, mi) => (
            <React.Fragment key={ml + mi}>
              <div className={`text-[10px] flex items-center ${isDark ? "text-slate-500" : "text-gray-400"}`}>{ml}</div>
              {years.map((y, yi) => {
                const cell = matrix[`${y}-${mi + 1}`];
                const intensity = cell?.intensity || 0;
                const color = YEAR_PALETTE[yi % YEAR_PALETTE.length];
                return (
                  <div
                    key={`${y}-${mi}`}
                    className="ciro-d2-heatmap-cell flex items-center justify-center text-[9px] font-medium tabular-nums"
                    style={{
                      animationDelay: `${(yi * 12 + mi) * 25}ms`,
                      backgroundColor: intensity > 0 ? `${color}${Math.round(intensity * 180 + 30).toString(16).padStart(2, "0")}` : (isDark ? "rgba(30,41,59,0.5)" : "rgba(241,245,249,0.9)"),
                      color: intensity > 0.45 ? "#fff" : (isDark ? "#94a3b8" : "#64748b"),
                    }}
                    title={cell ? `${cell.month_label} ${y}: ${formatCiroMoney(cell.total_ciro)}` : ""}
                  >
                    {cell && intensity > 0.15 ? formatCompactMoney(cell.total_ciro) : ""}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const CiroDashboard2Page = ({
  apiBase, isDark, textClass, labelClass, cardClass, inputClass,
  companies = [], isSuperAdmin = false, userCompanyId = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [companyFilter, setCompanyFilter] = useState(isSuperAdmin ? "all" : String(userCompanyId || ""));
  const [doctorFilter, setDoctorFilter] = useState("all");
  const [hospitalFilter, setHospitalFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [compareMode, setCompareMode] = useState("monthly");

  const glass = isDark ? "ciro-d2-glass" : "ciro-d2-glass-light";
  const chartTheme = isDark ? "ciro-d2-recharts-dark" : "ciro-d2-recharts-light";
  const axisColor = isDark ? "#64748b" : "#94a3b8";
  const tickColor = isDark ? "#94a3b8" : "#64748b";

  const scopedDoctors = useMemo(() => {
    if (!isSuperAdmin || companyFilter === "all") return doctors;
    return doctors.filter((d) => String(d.company_id) === String(companyFilter));
  }, [doctors, companyFilter, isSuperAdmin]);

  const scopedHospitals = useMemo(() => {
    if (!isSuperAdmin || companyFilter === "all") return hospitals;
    return hospitals.filter((h) => String(h.company_id) === String(companyFilter));
  }, [hospitals, companyFilter, isSuperAdmin]);

  const fetchMeta = useCallback(async () => {
    try {
      const params = {};
      if (isSuperAdmin && companyFilter && companyFilter !== "all") params.company_id = companyFilter;
      const [docRes, hospRes] = await Promise.all([
        axios.get(`${apiBase}/doctors`, { params }),
        axios.get(`${apiBase}/hospitals`, { params }),
      ]);
      setDoctors(docRes.data?.doctors || []);
      setHospitals(hospRes.data?.hospitals || []);
    } catch {
      setDoctors([]);
      setHospitals([]);
    }
  }, [apiBase, companyFilter, isSuperAdmin]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        dashboard: 1,
        deep_analytics: 1,
        trend_period: "monthly",
        search: searchTerm,
        doctor_id: doctorFilter,
        hospital_id: hospitalFilter,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      if (isSuperAdmin && companyFilter !== "all") params.company_id = companyFilter;
      const res = await axios.get(`${apiBase}/ciro`, { params });
      setData(res.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analitik yüklenemedi");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiBase, companyFilter, doctorFilter, endDate, hospitalFilter, isSuperAdmin, searchTerm, startDate]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const deep = data?.deep || {};
  const kpis = data?.kpis || {};
  const years = useMemo(() => deep.years || [], [deep.years]);
  const latestYoY = deep.yoy_growth?.[deep.yoy_growth.length - 1];
  const animReady = !loading && !!data;

  const animCiro = useAnimatedNumber(kpis.total_ciro, animReady);
  const animOps = useAnimatedNumber(kpis.total_operations, animReady);
  const animAvg = useAnimatedNumber(kpis.avg_ticket, animReady);
  const animPatients = useAnimatedNumber(kpis.unique_patients, animReady);

  const flowTrend = useMemo(
    () => (data?.trend || []).map((t) => ({
      label: t.period_label,
      ciro: Number(t.total_ciro) || 0,
      hastane: Number(t.hospital_payments) || 0,
      klinik: Number(t.doctor_payments) || 0,
    })),
    [data?.trend]
  );

  const yearlyChart = useMemo(
    () => (deep.yearly_summary || []).map((y) => ({
      year: String(y.year_label),
      ciro: Number(y.total_ciro) || 0,
      islem: Number(y.operation_count) || 0,
      hastane: Number(y.hospital_payments) || 0,
      klinik: Number(y.doctor_payments) || 0,
    })),
    [deep.yearly_summary]
  );

  const growthChart = useMemo(
    () => (deep.yoy_growth || []).map((g) => ({
      year: String(g.year),
      growth_pct: Number(g.growth_pct) || 0,
      ciro: Number(g.total_ciro) || 0,
      delta: Number(g.delta_amount) || 0,
    })),
    [deep.yoy_growth]
  );

  const doctorPatterns = deep.doctor_patterns || [];
  const doctorLeaderboard = useMemo(() => deep.doctor_leaderboard || [], [deep.doctor_leaderboard]);
  const doctorYoYRows = deep.doctor_yoy || [];

  const deepInsights = useMemo(() => {
    const lines = [...(data?.insights || [])];
    if (latestYoY) {
      lines.unshift(`${latestYoY.year} yılı cirosu bir önceki yıla göre %${Math.abs(latestYoY.growth_pct)} ${latestYoY.growth_pct >= 0 ? "artış" : "düşüş"} (${formatCiroMoney(latestYoY.delta_amount)}).`);
    }
    if (years.length >= 2) {
      lines.push(`${years[0]}–${years[years.length - 1]} arasında ${years.length} yıllık veri karşılaştırması aktif.`);
    }
    const topMonth = [...(deep.heatmap || [])].sort((a, b) => b.total_ciro - a.total_ciro)[0];
    if (topMonth) {
      lines.push(`En yüksek aylık performans: ${topMonth.month_label} ${topMonth.year} — ${formatCiroMoney(topMonth.total_ciro)}.`);
    }
    if (doctorLeaderboard.length >= 2) {
      const first = doctorLeaderboard[0];
      const second = doctorLeaderboard[1];
      lines.push(`${first.label} ciro lideri (%${first.share_pct}), ${second.label} ikinci sırada (${formatCiroMoney(second.total_ciro)}).`);
    }
    return lines.slice(0, 10);
  }, [data?.insights, deep.heatmap, doctorLeaderboard, latestYoY, years]);

  const doctorChartHeight = Math.max(280, Math.min(560, doctorYoYRows.length * 42));

  return (
    <div className={`ciro-d2-root space-y-4 ${chartTheme}`}>
      <div className="ciro-d2-grid-bg" />
      <div className="ciro-d2-content space-y-4">
        {/* Header */}
        <div className={`${glass} p-4 md:p-5`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="ciro-d2-pulse-dot" />
                <span className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                  Deep Analytics
                </span>
              </div>
              <h2 className={`text-xl font-bold flex items-center gap-2 ${textClass}`}>
                <Cpu className="w-5 h-5 text-cyan-400" />
                Ciro Dashboard 2
              </h2>
              <p className={`text-xs mt-1 max-w-xl ${labelClass}`}>
                Yıllık karşılaştırma, akan trend grafikleri, ısı haritası ve çok boyutlu doktor/hastane analizi
              </p>
              {data?.date_range?.min && (
                <p className={`text-[11px] mt-2 font-mono ${labelClass}`}>
                  {formatCiroDate(data.date_range.min)} → {formatCiroDate(data.date_range.max)}
                  {years.length ? ` · ${years.join(", ")}` : ""}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {["monthly", "yearly"].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setCompareMode(mode)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    compareMode === mode
                      ? "bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-lg shadow-emerald-500/20"
                      : isDark ? "bg-slate-800 text-slate-400 hover:text-white" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {mode === "monthly" ? "Aylık YoY" : "Yıllık Özet"}
                </button>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={fetchDashboard} disabled={loading} className="h-8">
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Yenile
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mt-5">
            <KpiTile label="Toplam Ciro" value={animCiro} tone="emerald" isDark={isDark} animate icon={Sparkles} delta={latestYoY?.growth_pct} />
            <KpiTile label="İşlem Hacmi" value={animOps} tone="sky" isDark={isDark} animate display={Math.round(animOps).toLocaleString("tr-TR")} icon={Activity} />
            <KpiTile label="Ort. İşlem Tutarı" value={animAvg} tone="violet" isDark={isDark} animate icon={Zap} />
            <KpiTile label="Benzersiz Hasta" value={animPatients} tone="amber" isDark={isDark} animate display={Math.round(animPatients).toLocaleString("tr-TR")} icon={Stethoscope} />
          </div>
        </div>

        {/* Filters */}
        <div className={`${glass} p-3`}>
          <div className="grid grid-cols-2 lg:grid-cols-7 gap-2 items-end">
            {isSuperAdmin && (
              <div>
                <Label className={`text-xs ${labelClass}`}>Firma</Label>
                <Select value={companyFilter} onValueChange={setCompanyFilter}>
                  <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="all">Tümü</SelectItem>
                    {companies.filter((c) => c.status === 1).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className={`text-xs ${labelClass}`}>Doktor</Label>
              <Select value={doctorFilter} onValueChange={setDoctorFilter}>
                <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="all">Tümü</SelectItem>
                  {scopedDoctors.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.ad_soyad}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={`text-xs ${labelClass}`}>Hastane</Label>
              <Select value={hospitalFilter} onValueChange={setHospitalFilter}>
                <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="all">Tümü</SelectItem>
                  {scopedHospitals.map((h) => <SelectItem key={h.id} value={String(h.id)}>{h.ad}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={`text-xs ${labelClass}`}>Başlangıç</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`h-8 text-xs ${inputClass}`} />
            </div>
            <div>
              <Label className={`text-xs ${labelClass}`}>Bitiş</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`h-8 text-xs ${inputClass}`} />
            </div>
            <div className="col-span-2 flex gap-2">
              <Input placeholder="Ara..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setSearchTerm(searchInput.trim())} className={`h-8 text-xs ${inputClass}`} />
              <Button type="button" size="sm" onClick={() => setSearchTerm(searchInput.trim())} className="h-8"><Search className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>

        {/* Prominent summary — top */}
        <SummaryHero
          insights={deepInsights}
          patterns={doctorPatterns}
          isDark={isDark}
          textClass={textClass}
          loading={loading && !data}
        />

        {loading && !data ? (
          <div className={`${glass} p-16 flex flex-col items-center justify-center gap-3`}>
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            <p className={`text-sm ${labelClass}`}>Analitik motoru verileri işliyor...</p>
          </div>
        ) : (
          <>
            {/* Doctor comparison — leaderboard + YoY all doctors */}
            <div className={`${glass} p-4 space-y-4`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h3 className={`text-sm font-semibold flex items-center gap-2 ${textClass}`}>
                  <Stethoscope className="w-4 h-4 text-violet-400" />
                  Doktor Kıyas & Başarı Örüntüleri
                  <span className={`text-[10px] font-normal ${labelClass}`}>({doctorYoYRows.length} doktor)</span>
                </h3>
                {deep.doctor_avg_ciro > 0 && (
                  <span className={`text-xs ${labelClass}`}>
                    Ort. doktor cirosu: <strong className={textClass}>{formatCiroMoney(deep.doctor_avg_ciro)}</strong>
                  </span>
                )}
              </div>

              <div className="overflow-x-auto rounded-xl border border-dashed border-slate-600/30">
                <table className="w-full text-xs min-w-[640px]">
                  <thead>
                    <tr className={isDark ? "text-slate-400 bg-slate-800/50" : "text-gray-500 bg-gray-50"}>
                      <th className="text-left py-2 px-3 font-semibold">#</th>
                      <th className="text-left py-2 px-3 font-semibold">Doktor</th>
                      <th className="text-right py-2 px-3 font-semibold">Toplam Ciro</th>
                      <th className="text-right py-2 px-3 font-semibold">Pay</th>
                      <th className="text-right py-2 px-3 font-semibold">İşlem</th>
                      <th className="text-right py-2 px-3 font-semibold">Ort. İşlem</th>
                      <th className="text-center py-2 px-3 font-semibold">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctorLeaderboard.map((row) => (
                      <tr key={`${row.doctor_id}-${row.label}`} className={`border-t ${isDark ? "border-slate-800 hover:bg-slate-800/40" : "border-gray-100 hover:bg-gray-50"}`}>
                        <td className="py-2 px-3 tabular-nums font-bold">{row.rank}</td>
                        <td className={`py-2 px-3 font-medium ${textClass}`}>{row.label}</td>
                        <td className="py-2 px-3 text-right tabular-nums font-semibold text-emerald-500">{formatCiroMoney(row.total_ciro)}</td>
                        <td className="py-2 px-3 text-right tabular-nums">%{row.share_pct}</td>
                        <td className="py-2 px-3 text-right tabular-nums">{row.operation_count}</td>
                        <td className="py-2 px-3 text-right tabular-nums">{formatCiroMoney(row.avg_ticket)}</td>
                        <td className="py-2 px-3 text-center">
                          {row.above_avg_ciro ? (
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>Ort. üstü</span>
                          ) : (
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] ${isDark ? "text-slate-500" : "text-gray-400"}`}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={`ciro-d2-chart-card ${glass}`}>
                <h4 className={`text-xs font-semibold mb-2 ${labelClass}`}>Doktor — Yıllara Göre Ciro (tüm doktorlar)</h4>
                <ResponsiveContainer width="100%" height={doctorChartHeight}>
                  <BarChart data={doctorYoYRows} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisColor} horizontal={false} />
                    <XAxis type="number" tick={{ fill: tickColor, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCompactMoney(v)} />
                    <YAxis type="category" dataKey="doctor" width={110} tick={{ fill: tickColor, fontSize: 9 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {years.map((y, i) => (
                      <Bar key={y} dataKey={String(y)} name={String(y)} fill={YEAR_PALETTE[i % YEAR_PALETTE.length]} radius={[0, 4, 4, 0]} animationDuration={1000 + i * 150} animationEasing={ANIM.easing} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Flow area + rolling 12 */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <div className={`ciro-d2-chart-card xl:col-span-2 ${glass}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-semibold flex items-center gap-2 ${textClass}`}>
                    <Activity className="w-4 h-4 text-emerald-400" /> Akan Ciro Trendi
                  </h3>
                  <span className={`text-[10px] ${labelClass}`}>Hastane + Klinik akış</span>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={flowTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="ciroFlowG" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" stopOpacity={0.45} />
                        <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="ciroHospG" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisColor} vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: tickColor, fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: tickColor, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCompactMoney(v)} width={48} />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                    <Area type="monotone" dataKey="ciro" name="Toplam" stroke="#34d399" fill="url(#ciroFlowG)" strokeWidth={2.5} animationDuration={ANIM.duration} animationEasing={ANIM.easing} />
                    <Area type="monotone" dataKey="hastane" name="Hastane" stroke="#38bdf8" fill="url(#ciroHospG)" strokeWidth={1.5} animationDuration={ANIM.duration + 200} animationEasing={ANIM.easing} />
                    <Line type="monotone" dataKey="klinik" name="Klinik" stroke="#a78bfa" strokeWidth={2} dot={false} animationDuration={ANIM.duration + 300} animationEasing={ANIM.easing} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className={`ciro-d2-chart-card ${glass}`}>
                <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textClass}`}>
                  <BarChart3 className="w-4 h-4 text-violet-400" /> 12 Aylık Hareketli Toplam
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={deep.rolling_12m || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="rollG" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.2} />
                        <stop offset="50%" stopColor="#22d3ee" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#34d399" stopOpacity={0.2} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisColor} vertical={false} />
                    <XAxis dataKey="period_label" tick={{ fill: tickColor, fontSize: 9 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: tickColor, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCompactMoney(v)} width={48} />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                    <Area type="monotone" dataKey="total_ciro" name="12A Toplam" stroke="#a78bfa" fill="url(#rollG)" strokeWidth={2.5} animationDuration={1600} animationEasing={ANIM.easing} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* YoY monthly vs yearly */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {(compareMode === "monthly" || compareMode === "yearly") && (
              <div className={`ciro-d2-chart-card ${compareMode === "monthly" ? "xl:col-span-2" : ""} ${glass}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-semibold ${textClass}`}>Yıl Bazlı Aylık Karşılaştırma (YoY)</h3>
                  <div className="flex gap-1">
                    {years.map((y, i) => (
                      <span key={y} className="ciro-d2-year-pill" style={{ backgroundColor: `${YEAR_PALETTE[i % YEAR_PALETTE.length]}22`, color: YEAR_PALETTE[i % YEAR_PALETTE.length] }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: YEAR_PALETTE[i % YEAR_PALETTE.length] }} />
                        {y}
                      </span>
                    ))}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={deep.yoy_monthly || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisColor} vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: tickColor, fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: tickColor, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCompactMoney(v)} width={48} />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {years.map((y, i) => (
                      <Line
                        key={y}
                        type="monotone"
                        dataKey={String(y)}
                        name={String(y)}
                        stroke={YEAR_PALETTE[i % YEAR_PALETTE.length]}
                        strokeWidth={2.5}
                        dot={{ r: 3 }}
                        activeDot={{ r: 6 }}
                        animationDuration={1200 + i * 200}
                        animationEasing={ANIM.easing}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              )}

              {(compareMode === "yearly" || compareMode === "monthly") && (
              <div className={`ciro-d2-chart-card ${compareMode === "yearly" ? "xl:col-span-2" : ""} ${glass}`}>
                <h3 className={`text-sm font-semibold mb-3 ${textClass}`}>Yıllık Ciro Büyümesi (YoY %)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={growthChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisColor} vertical={false} />
                    <XAxis dataKey="year" tick={{ fill: tickColor, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ fill: tickColor, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} width={40} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: tickColor, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCompactMoney(v)} width={48} />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                    <Bar yAxisId="left" dataKey="growth_pct" name="Büyüme %" radius={[6, 6, 0, 0]} animationDuration={1400} animationEasing={ANIM.easing}>
                      {growthChart.map((entry) => (
                        <Cell key={entry.year} fill={entry.growth_pct >= 0 ? "#34d399" : "#fb7185"} />
                      ))}
                    </Bar>
                    <Line yAxisId="right" type="monotone" dataKey="ciro" name="Ciro" stroke="#38bdf8" strokeWidth={2.5} dot animationDuration={1600} animationEasing={ANIM.easing} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              )}
            </div>

            {/* Heatmap + yearly bars */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className={`ciro-d2-chart-card ${glass}`}>
                <h3 className={`text-sm font-semibold mb-3 ${textClass}`}>Ay × Yıl Isı Haritası</h3>
                <HeatmapGrid cells={deep.heatmap} years={years} isDark={isDark} />
              </div>

              <div className={`ciro-d2-chart-card ${glass}`}>
                <h3 className={`text-sm font-semibold mb-3 ${textClass}`}>Yıllık Toplam Ciro Kırılımı</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={yearlyChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisColor} vertical={false} />
                    <XAxis dataKey="year" tick={{ fill: tickColor, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: tickColor, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCompactMoney(v)} width={48} />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="hastane" name="Hastane" stackId="a" fill="#38bdf8" radius={[0, 0, 0, 0]} animationDuration={1200} animationEasing={ANIM.easing} />
                    <Bar dataKey="klinik" name="Klinik" stackId="a" fill="#a78bfa" radius={[6, 6, 0, 0]} animationDuration={1400} animationEasing={ANIM.easing} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Radar — keep compact doctor multi-dim */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <div className={`ciro-d2-chart-card xl:col-span-2 ${glass}`}>
                <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textClass}`}>
                  <BarChart3 className="w-4 h-4 text-violet-400" /> Doktor Ciro Pay Dağılımı
                </h3>
                <ResponsiveContainer width="100%" height={Math.max(220, doctorLeaderboard.length * 28)}>
                  <BarChart data={doctorLeaderboard} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisColor} horizontal={false} />
                    <XAxis type="number" tick={{ fill: tickColor, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCompactMoney(v)} />
                    <YAxis type="category" dataKey="label" width={110} tick={{ fill: tickColor, fontSize: 9 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                    <Bar dataKey="total_ciro" name="Toplam Ciro" radius={[0, 6, 6, 0]} animationDuration={1200} animationEasing={ANIM.easing}>
                      {doctorLeaderboard.map((_, i) => (
                        <Cell key={i} fill={YEAR_PALETTE[i % YEAR_PALETTE.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className={`ciro-d2-chart-card ${glass}`}>
                <h3 className={`text-sm font-semibold mb-3 ${textClass}`}>Doktor Radar — Çok Boyutlu</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={deep.radar_doctors || []} cx="50%" cy="50%" outerRadius="72%">
                    <PolarGrid stroke={axisColor} />
                    <PolarAngleAxis dataKey="doctor" tick={{ fill: tickColor, fontSize: 9 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                    <Radar name="Skor" dataKey="ciro" stroke="#34d399" fill="#34d399" fillOpacity={0.35} animationDuration={1500} animationEasing={ANIM.easing} />
                    <Radar name="İşlem" dataKey="islem" stroke="#38bdf8" fill="#38bdf8" fillOpacity={0.2} animationDuration={1700} animationEasing={ANIM.easing} />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Hospital YoY + Quarterly */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className={`ciro-d2-chart-card ${glass}`}>
                <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textClass}`}>
                  <Building2 className="w-4 h-4 text-sky-400" /> Hastane — Yıllık Kıyas
                </h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={deep.hospital_yoy || []} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisColor} horizontal={false} />
                    <XAxis type="number" tick={{ fill: tickColor, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCompactMoney(v)} />
                    <YAxis type="category" dataKey="hospital" width={88} tick={{ fill: tickColor, fontSize: 9 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                    {years.map((y, i) => (
                      <Bar key={y} dataKey={String(y)} name={String(y)} fill={YEAR_PALETTE[i % YEAR_PALETTE.length]} stackId="h" animationDuration={1100 + i * 120} animationEasing={ANIM.easing} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className={`ciro-d2-chart-card ${glass}`}>
                <h3 className={`text-sm font-semibold mb-3 ${textClass}`}>Çeyreklik Trend</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={deep.quarterly_trend || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="qGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisColor} vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: tickColor, fontSize: 9 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: tickColor, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCompactMoney(v)} width={48} />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                    <Area type="stepAfter" dataKey="total_ciro" name="Ciro" stroke="#22d3ee" fill="url(#qGrad)" strokeWidth={2} animationDuration={1300} animationEasing={ANIM.easing} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CiroDashboard2Page;
